import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { ParkettStar } from "../components/ParkettStar";
import { AWARDS, PARKETT_STARS, IMG } from "../data/content";
import { Leaf, Award, ShieldCheck, ArrowRight } from "lucide-react";

const AWARD_ICONS = [ShieldCheck, Award, Leaf];

export const Awards = () => (
  <section data-testid="awards-section" id="auszeichnungen" className="relative wood-band text-white py-20 lg:py-32 overflow-hidden">
    <div className="absolute inset-0 opacity-15">
      <img src={IMG.cork} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="container-blum relative z-10">
      {/* Parkett Star Showcase */}
      <div className="mb-16 lg:mb-24">
        <Reveal>
          <p className="overline text-[#D7C4B1]">Mehrfach ausgezeichnet</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight max-w-2xl">
            Ausgezeichnet für gelebtes Handwerk.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-white/70 text-lg leading-relaxed mt-5 max-w-2xl">
            Unsere Arbeit wird regelmäßig honoriert: Neben dem mehrfach verliehenen Parkett Star
            durften wir 2024 auch den renommierten „Boden Zukunft Award" entgegennehmen – ein Beleg
            dafür, dass Ausbildung, Generationenverantwortung und Qualität bei uns keine Floskeln sind.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-12">
          {PARKETT_STARS.map((s, i) => (
            <Reveal key={s.year} delay={i * 0.1}>
              <div
                data-testid={`parkett-star-${s.year}`}
                className="flex flex-col items-center text-center bg-white/5 border border-white/10 p-8 lg:p-10 h-full hover:bg-white/[0.08] transition-colors"
              >
                <ParkettStar year={s.year} size={140} />
                <h3 className="font-display text-2xl font-bold text-[#EEC150] mt-6">Parkett Star {s.year}</h3>
                <p className="text-white/80 mt-2 leading-relaxed">{s.area}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 mb-6">
              <Leaf className="w-4 h-4 text-[#D7C4B1]" />
              <span className="text-xs uppercase tracking-[0.18em] font-display">Nachhaltigkeit · NCheck-360</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
              Haltung statt nur Zertifikat.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/75 text-lg leading-relaxed mt-6">
              Naturmaterialien wie Parkett, Kork und Linoleum verdienen einen verantwortungsvollen Umgang.
              Mit der erfolgreich absolvierten NCheck-360-Zertifizierung machen wir unsere Nachhaltigkeit
              messbar – und leben sie täglich: regional, langlebig, reparierbar statt wegwerfbar.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div data-testid="sustain-seal" className="mt-8 inline-flex bg-white p-5 border border-white/10 max-w-xs">
              <img src={IMG.sustainSeal} alt="Nachhaltigkeit im Handwerk 360°" className="h-16 w-auto" />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <p className="overline text-[#D7C4B1] mb-6">Ausgezeichnete Qualität</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="tuev-badge" className="flex items-center gap-5 bg-white p-5 sm:p-6 mb-5 border border-white/10">
              <img src={IMG.tuev} alt="TÜV Rheinland – Fachbetrieb für Fußbodentechnik" className="h-20 sm:h-24 w-auto shrink-0" />
              <div>
                <h3 className="font-display text-lg font-semibold text-[#2B2B30]">TÜV Rheinland zertifiziert</h3>
                <p className="text-[#6B6B73] text-sm mt-1">Geprüfter Fachbetrieb für Fußbodentechnik – Ihre Sicherheit, schriftlich bestätigt.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <div data-testid="boden-zukunft-badge" className="flex items-center gap-5 bg-white p-5 sm:p-6 mb-5 border border-white/10">
              <img src={IMG.bodenZukunft} alt="Ausgezeichnet mit dem Boden Zukunft Award" className="h-14 sm:h-16 w-auto shrink-0" />
              <div>
                <h3 className="font-display text-lg font-semibold text-[#2B2B30]">Boden Zukunft Award 2024</h3>
                <p className="text-[#6B6B73] text-sm mt-1">Ausgezeichnet von den Fachzeitschriften BTH Heimtex, FussbodenTechnik und Parkett Magazin.</p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {AWARDS.map((a, i) => {
              const Icon = AWARD_ICONS[i];
              return (
                <Reveal key={a.title} delay={i * 0.07}>
                  <div data-testid={`award-${i}`} className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors h-full">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#D32F2F] shrink-0">
                      <Icon className="w-6 h-6 text-white" strokeWidth={1.7} />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-semibold">{a.title}</h3>
                      <p className="text-white/65 text-sm mt-1">{a.sub}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>

      <Reveal delay={0.2}>
        <div className="flex justify-center mt-14 lg:mt-16">
          <Link to="/unternehmen/auszeichnungen" data-testid="awards-more-link" className="btn-base btn-red">
            Mehr zu unseren Auszeichnungen <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
